/**
 * @file Système générique d'événements cross-tabs pour toute l'application.
 * @module common.utils.appEventSync
 *
 * Permet de synchroniser les données entre plusieurs onglets/fenêtres
 * et de notifier les composants lors de modifications de données.
 * Utilise localStorage pour la communication cross-tabs et
 * des CustomEvents pour la communication dans le même onglet.
 *
 * Chaque module métier expose des fonctions raccourcis (notifyXxxUpdated)
 * qui délèguent à `notifyDataChange`. Côté consommateur, `createEventListeners`
 * enregistre les listeners storage + CustomEvent et renvoie une fonction de cleanup.
 */

/** Types d'événements disponibles, organisés par module métier. */
export const APP_EVENTS = {
  // Module Restaurant
  RESTAURANT: {
    TABLES_UPDATED: 'restaurant:tables',
    AREAS_UPDATED: 'restaurant:areas',
    COMBINATIONS_UPDATED: 'restaurant:combinations',
    FLOOR_PLAN_UPDATED: 'restaurant:floorplan',
    RESERVATIONS_UPDATED: 'restaurant:reservations',
    REVIEWS_UPDATED: 'restaurant:reviews',
    SERVICES_UPDATED: 'restaurant:services',
    CONFIG_UPDATED: 'restaurant:config',
    AVAILABILITY_UPDATED: 'restaurant:availability',
    FORMULAS_UPDATED: 'restaurant:formulas',
    DAY_MEMOS_UPDATED: 'restaurant:daymemos',
    MESSAGES_UPDATED: 'restaurant:messages',
  },

  // Module Wellness
  WELLNESS: {
    BOOKINGS_UPDATED: 'wellness:bookings',
    SERVICES_UPDATED: 'wellness:services',
    SERVICE_CATEGORIES_UPDATED: 'wellness:servicecategories',
    RESOURCES_UPDATED: 'wellness:resources',
    SPACES_UPDATED: 'wellness:spaces',
    SPACE_GROUPS_UPDATED: 'wellness:spacegroups',
    EXTRAS_UPDATED: 'wellness:extras',
    EXTRA_CATEGORIES_UPDATED: 'wellness:extracategories',
    PRODUCTS_UPDATED: 'wellness:products',
    PRODUCT_CATEGORIES_UPDATED: 'wellness:productcategories',
    BREAKS_UPDATED: 'wellness:breaks',
    SCHEDULES_UPDATED: 'wellness:schedules',
    DAILY_CLOSINGS_UPDATED: 'wellness:dailyclosings',
    DAILY_MEMOS_UPDATED: 'wellness:dailymemos',
    AVAILABILITY_UPDATED: 'wellness:availability',
    CONFIG_UPDATED: 'wellness:config',
  },

  // Module Gift Vouchers
  GIFT_VOUCHERS: {
    LIST_UPDATED: 'giftvouchers:list',
    CATALOG_UPDATED: 'giftvouchers:catalog',
    CATEGORIES_UPDATED: 'giftvouchers:categories',
    OPTIONS_UPDATED: 'giftvouchers:options',
    PROMOTIONS_UPDATED: 'giftvouchers:promotions',
    SETTINGS_UPDATED: 'giftvouchers:settings',
    STATS_UPDATED: 'giftvouchers:stats',
  },

  // Module Employees
  EMPLOYEES: {
    LIST_UPDATED: 'employees:list',
    SKILLS_UPDATED: 'employees:skills',
    SCHEDULES_UPDATED: 'employees:schedules',
  },

  // Module Customers
  CUSTOMERS: {
    LIST_UPDATED: 'customers:list',
    DETAILS_UPDATED: 'customers:details',
    CONFIG_UPDATED: 'customers:config',
  },

  // Module Invoices
  INVOICES: {
    LIST_UPDATED: 'invoices:list',
    DETAILS_UPDATED: 'invoices:details',
  },

  // Module Company
  COMPANY: {
    DETAILS_UPDATED: 'company:details',
    SHARING_UPDATED: 'company:sharing',
  },

  // Module Permissions
  PERMISSIONS: {
    UPDATED: 'permissions:updated',
  },

  // Module Subscriptions (app level)
  SUBSCRIPTIONS: {
    LIST_UPDATED: 'subscriptions:list',
  },

  // Module Users (app level)
  USERS: {
    LIST_UPDATED: 'users:list',
  },

  // Module Newsletter
  NEWSLETTER: {
    CAMPAIGNS_UPDATED: 'newsletter:campaigns',
  },

  // Module Admin
  ADMIN: {
    ACCOUNTS_UPDATED: 'admin:accounts',
    COMPANIES_UPDATED: 'admin:companies',
    INVOICES_UPDATED: 'admin:invoices',
    SUBSCRIPTIONS_UPDATED: 'admin:subscriptions',
    PROSPECTS_UPDATED: 'admin:prospects',
    USERS_UPDATED: 'admin:users',
    GIFT_VOUCHERS_UPDATED: 'admin:giftvouchers',
    GIFT_VOUCHER_ANALYTICS_UPDATED: 'admin:giftVoucherAnalytics',
    EMAIL_LOGS_UPDATED: 'admin:emaillogs',
    COMMISSIONS_UPDATED: 'admin:commissions',
    TEAM_UPDATED: 'admin:team',
    SETTINGS_UPDATED: 'admin:settings',
    INTEGRATION_KEYS_UPDATED: 'admin:integrationKeys',
  },
};

const STORAGE_PREFIX = 'brydge_sync_';
const EVENT_PREFIX = 'brydge:sync:';

/**
 * Génère une clé de stockage unique pour un événement
 *
 * @param {string} companyId - ID de l'entreprise (optionnel pour admin)
 * @param {string} eventType - Type d'événement
 * @return {string} Clé de stockage
 */
export const getStorageKey = (companyId, eventType) => {
  if (companyId) {
    return `${STORAGE_PREFIX}${companyId}_${eventType}`;
  }
  return `${STORAGE_PREFIX}${eventType}`;
};

/**
 * Génère un nom d'événement custom
 *
 * @param {string} eventType - Type d'événement
 * @return {string} Nom de l'événement custom
 */
export const getCustomEventName = (eventType) => {
  return `${EVENT_PREFIX}${eventType}`;
};

/**
 * Notifie tous les onglets d'un changement de données
 *
 * Envoie à la fois un CustomEvent pour le même onglet
 * et un événement localStorage pour les autres onglets.
 *
 * @param {string} companyId - ID de l'entreprise (null pour admin global)
 * @param {string} eventType - Type d'événement (depuis APP_EVENTS)
 * @param {Object} data - Données supplémentaires (optionnel)
 */
export const notifyDataChange = (companyId, eventType, data = {}) => {
  if (typeof window === 'undefined') return;

  const timestamp = Date.now();
  const payload = {
    ...data,
    timestamp,
    companyId,
    eventType,
  };

  // CustomEvent pour le même onglet, localStorage pour les autres
  const customEvent = new CustomEvent(getCustomEventName(eventType), {
    detail: payload,
  });
  window.dispatchEvent(customEvent);

  const storageKey = getStorageKey(companyId, eventType);
  try {
    localStorage.setItem(storageKey, JSON.stringify(payload));
    // Nettoyage rapide pour éviter l'accumulation dans le localStorage
    setTimeout(() => {
      try {
        localStorage.removeItem(storageKey);
      } catch (e) {
        // Ignore : le nettoyage n'est pas critique
      }
    }, 100);
  } catch (error) {
    // localStorage peut échouer en navigation privée ou si le quota est dépassé
    console.warn('Failed to notify cross-tabs:', error);
  }
};

// ============================================
// Fonctions utilitaires par module - Restaurant
// ============================================

/**
 * Signale qu'une campagne a changé, pour que les autres onglets se remettent à jour.
 *
 * @param {string} companyId - Établissement concerné
 * @param {Object} [data] - Détail éventuel
 * @return {void}
 */
export const notifyNewsletterCampaignsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.NEWSLETTER.CAMPAIGNS_UPDATED, data);
};

export const notifyTablesUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.RESTAURANT.TABLES_UPDATED, data);
};

export const notifyAreasUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.RESTAURANT.AREAS_UPDATED, data);
};

export const notifyCombinationsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.RESTAURANT.COMBINATIONS_UPDATED, data);
};

export const notifyFloorPlanUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.RESTAURANT.FLOOR_PLAN_UPDATED, data);
};

export const notifyReservationsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.RESTAURANT.RESERVATIONS_UPDATED, data);
};

export const notifyRestaurantReviewsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.RESTAURANT.REVIEWS_UPDATED, data);
};

export const notifyRestaurantServicesUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.RESTAURANT.SERVICES_UPDATED, data);
};

export const notifyRestaurantConfigUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.RESTAURANT.CONFIG_UPDATED, data);
};

export const notifyRestaurantAvailabilityUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.RESTAURANT.AVAILABILITY_UPDATED, data);
};

export const notifyFormulasUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.RESTAURANT.FORMULAS_UPDATED, data);
};

export const notifyDayMemosUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.RESTAURANT.DAY_MEMOS_UPDATED, data);
};

export const notifyRestaurantMessagesUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.RESTAURANT.MESSAGES_UPDATED, data);
};

// ============================================
// Fonctions utilitaires par module - Wellness
// ============================================

export const notifyWellnessBookingsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.WELLNESS.BOOKINGS_UPDATED, data);
};

export const notifyWellnessServicesUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.WELLNESS.SERVICES_UPDATED, data);
};

export const notifyWellnessServiceCategoriesUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.WELLNESS.SERVICE_CATEGORIES_UPDATED, data);
};

export const notifyWellnessResourcesUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.WELLNESS.RESOURCES_UPDATED, data);
};

export const notifyWellnessSpacesUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.WELLNESS.SPACES_UPDATED, data);
};

export const notifyWellnessSpaceGroupsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.WELLNESS.SPACE_GROUPS_UPDATED, data);
};

export const notifyWellnessExtrasUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.WELLNESS.EXTRAS_UPDATED, data);
};

export const notifyWellnessExtraCategoriesUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.WELLNESS.EXTRA_CATEGORIES_UPDATED, data);
};

export const notifyWellnessProductsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.WELLNESS.PRODUCTS_UPDATED, data);
};

export const notifyWellnessProductCategoriesUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.WELLNESS.PRODUCT_CATEGORIES_UPDATED, data);
};

export const notifyWellnessBreaksUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.WELLNESS.BREAKS_UPDATED, data);
};

export const notifyWellnessConfigUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.WELLNESS.CONFIG_UPDATED, data);
};

export const notifyWellnessSchedulesUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.WELLNESS.SCHEDULES_UPDATED, data);
};

export const notifyDailyClosingsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.WELLNESS.DAILY_CLOSINGS_UPDATED, data);
};

export const notifyWellnessAvailabilityUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.WELLNESS.AVAILABILITY_UPDATED, data);
};

export const notifyWellnessDailyMemosUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.WELLNESS.DAILY_MEMOS_UPDATED, data);
};

// ============================================
// Fonctions utilitaires par module - Gift Vouchers
// ============================================

export const notifyGiftVouchersListUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.GIFT_VOUCHERS.LIST_UPDATED, data);
};

export const notifyGiftVouchersCatalogUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.GIFT_VOUCHERS.CATALOG_UPDATED, data);
};

export const notifyGiftVouchersCategoriesUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.GIFT_VOUCHERS.CATEGORIES_UPDATED, data);
};

export const notifyGiftVouchersOptionsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.GIFT_VOUCHERS.OPTIONS_UPDATED, data);
};

export const notifyGiftVouchersPromotionsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.GIFT_VOUCHERS.PROMOTIONS_UPDATED, data);
};

export const notifyGiftVouchersSettingsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.GIFT_VOUCHERS.SETTINGS_UPDATED, data);
};

export const notifyGiftVouchersStatsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.GIFT_VOUCHERS.STATS_UPDATED, data);
};

// ============================================
// Fonctions utilitaires par module - Employees
// ============================================

export const notifyEmployeesListUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.EMPLOYEES.LIST_UPDATED, data);
};

export const notifyEmployeesSkillsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.EMPLOYEES.SKILLS_UPDATED, data);
};

export const notifyEmployeesSchedulesUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.EMPLOYEES.SCHEDULES_UPDATED, data);
};

// ============================================
// Fonctions utilitaires par module - Customers
// ============================================

export const notifyCustomersListUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.CUSTOMERS.LIST_UPDATED, data);
};

export const notifyCustomerDetailsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.CUSTOMERS.DETAILS_UPDATED, data);
};

export const notifyCustomerConfigUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.CUSTOMERS.CONFIG_UPDATED, data);
};

// ============================================
// Fonctions utilitaires par module - Invoices
// ============================================

export const notifyInvoicesListUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.INVOICES.LIST_UPDATED, data);
};

export const notifyInvoiceDetailsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.INVOICES.DETAILS_UPDATED, data);
};

// ============================================
// Fonctions utilitaires par module - Company
// ============================================

export const notifyCompanyDetailsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.COMPANY.DETAILS_UPDATED, data);
};

export const notifyCompanySharingUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.COMPANY.SHARING_UPDATED, data);
};

// ============================================
// Fonctions utilitaires par module - Subscriptions
// ============================================

export const notifyPermissionsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.PERMISSIONS.UPDATED, data);
};

export const notifySubscriptionsUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.SUBSCRIPTIONS.LIST_UPDATED, data);
};

// ============================================
// Fonctions utilitaires par module - Users
// ============================================

export const notifyUsersListUpdated = (companyId, data = {}) => {
  notifyDataChange(companyId, APP_EVENTS.USERS.LIST_UPDATED, data);
};

// ============================================
// Fonctions utilitaires par module - Admin
// ============================================

export const notifyAdminAccountsUpdated = (data = {}) => {
  notifyDataChange(null, APP_EVENTS.ADMIN.ACCOUNTS_UPDATED, data);
};

export const notifyAdminCompaniesUpdated = (data = {}) => {
  notifyDataChange(null, APP_EVENTS.ADMIN.COMPANIES_UPDATED, data);
};

export const notifyAdminInvoicesUpdated = (data = {}) => {
  notifyDataChange(null, APP_EVENTS.ADMIN.INVOICES_UPDATED, data);
};

export const notifyAdminSubscriptionsUpdated = (data = {}) => {
  notifyDataChange(null, APP_EVENTS.ADMIN.SUBSCRIPTIONS_UPDATED, data);
};

export const notifyAdminProspectsUpdated = (data = {}) => {
  notifyDataChange(null, APP_EVENTS.ADMIN.PROSPECTS_UPDATED, data);
};

export const notifyAdminUsersUpdated = (data = {}) => {
  notifyDataChange(null, APP_EVENTS.ADMIN.USERS_UPDATED, data);
};

export const notifyAdminGiftVouchersUpdated = (data = {}) => {
  notifyDataChange(null, APP_EVENTS.ADMIN.GIFT_VOUCHERS_UPDATED, data);
};

export const notifyAdminEmailLogsUpdated = (data = {}) => {
  notifyDataChange(null, APP_EVENTS.ADMIN.EMAIL_LOGS_UPDATED, data);
};

export const notifyAdminCommissionsUpdated = (data = {}) => {
  notifyDataChange(null, APP_EVENTS.ADMIN.COMMISSIONS_UPDATED, data);
};

export const notifyAdminTeamUpdated = (data = {}) => {
  notifyDataChange(null, APP_EVENTS.ADMIN.TEAM_UPDATED, data);
};

/**
 * Signale un changement des clés d'intégration d'un établissement.
 *
 * Distinct de `notifyAdminCompaniesUpdated` : une émission ou une révocation de
 * clé ne modifie pas la fiche, et la recharger réinitialiserait les saisies en
 * cours de l'onglet Administration.
 *
 * @param {Object} [data] - Contexte de l'événement
 * @return {void}
 */
export const notifyAdminIntegrationKeysUpdated = (data = {}) => {
  notifyDataChange(null, APP_EVENTS.ADMIN.INTEGRATION_KEYS_UPDATED, data);
};

export const notifyAdminSettingsUpdated = (data = {}) => {
  notifyDataChange(null, APP_EVENTS.ADMIN.SETTINGS_UPDATED, data);
};

// ============================================
// Hook helper pour écouter les événements
// ============================================

/** Configuration par défaut pour SWR avec revalidation optimisée. */
export const DEFAULT_SWR_CONFIG = {
  revalidateOnFocus: true,
  revalidateOnReconnect: true,
  dedupingInterval: 2000,
  focusThrottleInterval: 5000,
};

/**
 * Crée les listeners pour les événements cross-tabs
 *
 * @param {string} companyId - ID de l'entreprise
 * @param {string} eventType - Type d'événement à écouter
 * @param {Function} onEvent - Callback à appeler lors d'un événement
 * @return {Function} Fonction de cleanup
 */
export const createEventListeners = (companyId, eventType, onEvent) => {
  if (typeof window === 'undefined') return () => {};

  const storageKey = getStorageKey(companyId, eventType);
  const customEventName = getCustomEventName(eventType);

  const handleStorageChange = (event) => {
    if (event.key === storageKey) {
      onEvent(event);
    }
  };

  const handleCustomEvent = (event) => {
    onEvent(event);
  };

  window.addEventListener('storage', handleStorageChange);
  window.addEventListener(customEventName, handleCustomEvent);

  return () => {
    window.removeEventListener('storage', handleStorageChange);
    window.removeEventListener(customEventName, handleCustomEvent);
  };
};
